import React from 'react';
import { ASIM } from 'consts';
import api from 'services/api';

/* Contexto React que carrega os dados de sessão e permissões do usuário */
const PermissionContext = React.createContext(null);

/* Lê os dados de sessão do usuário armazenados no sessionStorage */
function readSessionData() {
  return {
    codusuario: sessionStorage.getItem('codUser'),
    nomeUsuario: (sessionStorage.getItem('usuario') || sessionStorage.getItem('user') || '').trim().toUpperCase(),
    isTechnicalFlag: sessionStorage.getItem('isTechnicalUser')
  };
};

/* Provider que disponibiliza sessão, permissões por módulo e helpers */
export function PermissionProvider({ children }) {
  const [sessionData, setSessionData] = React.useState(readSessionData());
  const [permissionsByModule, setPermissionsByModule] = React.useState({});
  const [loadingByModule, setLoadingByModule] = React.useState({});

  const { codusuario, nomeUsuario, isTechnicalFlag } = sessionData;
  const isTechnicalUser = isTechnicalFlag === ASIM || nomeUsuario === 'ADOISX';

  const refreshSession = React.useCallback(() => {
    setSessionData(readSessionData());
  }, []);

  const loadPermissions = React.useCallback(async (
    codUsuarioParam,
    codModuloParam,
    forceReload = false
  ) => {
    if (!codUsuarioParam || !codModuloParam) {
      return [];
    }

    const moduleKey = String(codModuloParam);

    if (!forceReload && Object.prototype.hasOwnProperty.call(permissionsByModule, moduleKey)) {
      return permissionsByModule[moduleKey] || [];
    }

    if (isTechnicalUser) {
      const allPerms = ['__ALL__'];

      setPermissionsByModule((prev) => ({
        ...prev,
        [moduleKey]: allPerms
      }));

      setLoadingByModule((prev) => ({
        ...prev,
        [moduleKey]: false
      }));

      return allPerms;
    }

    setLoadingByModule((prev) => ({
      ...prev,
      [moduleKey]: true
    }));

    try {
      const response = await api.get(`/usuario/permissoes/${codUsuarioParam}?codmodulo=${moduleKey}`);

      const rawList = Array.isArray(
        response?.data?.permissions
      ) ? response.data.permissions : Array.isArray(
        response?.data?.data?.[0]?.permissions
      ) ? response.data.data[0].permissions : [];

      const list = rawList.map((p) => Number(p)).filter((p) => Number.isFinite(p));

      setPermissionsByModule((prev) => ({
        ...prev,
        [moduleKey]: list
      }));

      return list;
    } catch (err) {
      console.error('Erro ao buscar as permissões:', err);

      setPermissionsByModule((prev) => ({
        ...prev,
        [moduleKey]: []
      }));

      return [];
    } finally {
      setLoadingByModule((prev) => ({
        ...prev,
        [moduleKey]: false
      }));
    }
  }, [isTechnicalUser, permissionsByModule]);

  const hasPerm = React.useCallback((codModuloParam, permCode) => {
    if (!permCode) return true;
    if (isTechnicalUser) return true;

    const moduleKey = String(codModuloParam);
    const modulePerms = permissionsByModule[moduleKey] || [];
    const permSet = new Set(modulePerms.map((p) => Number(p)));
    const result = permSet.has(Number(permCode));

    return result;
  }, [isTechnicalUser, permissionsByModule]);

  const isLoading = React.useCallback((codModuloParam) => {
    const moduleKey = String(codModuloParam);

    return !!loadingByModule[moduleKey];
  }, [loadingByModule]);

  const clearModulePermissions = React.useCallback((codModuloParam) => {
    const moduleKey = String(codModuloParam);

    setPermissionsByModule((prev) => {
      const next = { ...prev };
      delete next[moduleKey];
      return next;
    });

    setLoadingByModule((prev) => {
      const next = { ...prev };
      delete next[moduleKey];
      return next;
    });
  }, []);

  const clearAllPermissions = React.useCallback(() => {
    setPermissionsByModule({});
    setLoadingByModule({});
  }, []);

  const value = React.useMemo(
    () => ({
      codusuario,
      nomeUsuario,
      isTechnicalUser,
      permissionsByModule,
      loadPermissions,
      hasPerm,
      isLoading,
      clearModulePermissions,
      clearAllPermissions,
      refreshSession
    }),
    [
      codusuario,
      nomeUsuario,
      isTechnicalUser,
      permissionsByModule,
      loadPermissions,
      hasPerm,
      isLoading,
      clearModulePermissions,
      clearAllPermissions,
      refreshSession
    ]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

/* Hook de acesso ao PermissionContext */
export function usePermissionContext() {
  const context = React.useContext(PermissionContext);

  if (!context) {
    throw new Error('usePermissionContext deve ser usado dentro de PermissionProvider!');
  }

  return context;
};